/**
 * E8. Пирамида: раз в жизнь в 20–50 лет «знакомый» предлагает «вложение
 * под 40 % в год». Шанс предложения в год — `pyramidOfferP`: выше при
 * низкой удовлетворённости жизнью (`lifeSatisfaction`) и высоком Джини;
 * жребий детерминирован seed'ом жизни и возрастом (`createRng`, не
 * Math.random). При деньгах 0 не предлагается (Review Focus 4).
 *
 * Ввод: `{ invest }` (до 100 % сбережений) или `{ decline: true }`.
 * Вложение уходит из денег и ставит флаги `pyramidInvested`/`pyramidYears`;
 * дальше считает движок (`serviceHoldings`): годы 1–2 — +40 %, год 3 —
 * с p 0,85 крах (вложенное −100 %, тяга +10, счастье −10), с p 0,15 выход
 * с прибылью. Отказ и «пропустить» лишь помечают `pyramidOffered`.
 * Вариант «спросить N» (когорта) — план 4.
 */
import { getValue, type Dataset, type GamblingLaw } from '@life/data';
import {
  createRng,
  FLAG_PYRAMID_INVESTED,
  FLAG_PYRAMID_OFFERED,
  FLAG_PYRAMID_YEARS,
  PYRAMID_CRASH_P,
  PYRAMID_YIELD,
  type Life,
  type MinigameOutcome,
} from '@life/engine';
import type { ExpectancyText, MinigameDef } from '../types';
import { clampBet } from '../casino/common';

export const PYRAMID_MIN_AGE = 20;
export const PYRAMID_MAX_AGE = 50;
/** Базовый шанс предложения в год и надбавки за недовольство и неравенство. */
export const PYRAMID_OFFER_BASE = 0.02;
export const PYRAMID_OFFER_SATISFACTION_WEIGHT = 0.05;
export const PYRAMID_OFFER_GINI_WEIGHT = 0.1;
export const PYRAMID_OFFER_MAX = 0.15;

export interface PyramidState {
  maxInvest: number;
  /** Обещанная доходность в год (0,4). */
  promisedYield: number;
  /** Шанс краха на третий год. */
  crashP: number;
}

export type PyramidInput = { invest: number } | { decline: true };

function indicatorOr(ds: Dataset, life: Life, indicator: 'lifeSatisfaction' | 'gini', fallback: number): number {
  try {
    return getValue(ds, life.iso3, indicator, life.birthYear + life.age).v;
  } catch {
    return fallback;
  }
}

/** Шанс предложения пирамиды в год: 0,02 + 0,05·(1 − удовлетворённость/10) + 0,1·max(0, Джини − 0,3), не больше 0,15. */
export function pyramidOfferP(ds: Dataset, life: Life): number {
  const satisfaction = Math.min(10, Math.max(0, indicatorOr(ds, life, 'lifeSatisfaction', 5)));
  const gini = Math.min(1, Math.max(0, indicatorOr(ds, life, 'gini', 0.38)));
  const p = PYRAMID_OFFER_BASE + PYRAMID_OFFER_SATISFACTION_WEIGHT * (1 - satisfaction / 10) + PYRAMID_OFFER_GINI_WEIGHT * Math.max(0, gini - 0.3);
  return Math.min(PYRAMID_OFFER_MAX, p);
}

function offeredFlags(): Record<string, boolean> {
  return { [FLAG_PYRAMID_OFFERED]: true };
}

export const pyramid: MinigameDef<PyramidInput, PyramidState> = {
  id: 'pyramid',
  section: 'E',
  triggers: [{ kind: 'age', min: PYRAMID_MIN_AGE, max: PYRAMID_MAX_AGE }],
  available(life: Life, ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < PYRAMID_MIN_AGE || life.age > PYRAMID_MAX_AGE) return false;
    if (!(life.money > 0)) return false;
    if (life.flags[FLAG_PYRAMID_OFFERED] === true || FLAG_PYRAMID_INVESTED in life.flags) return false;
    return createRng(`${life.seed}/pyramid/${life.age}`).chance(pyramidOfferP(ds, life));
  },
  setup(life: Life): PyramidState {
    return { maxInvest: Math.max(0, life.money), promisedYield: PYRAMID_YIELD, crashP: PYRAMID_CRASH_P };
  },
  play(state: PyramidState, input: PyramidInput, life: Life): MinigameOutcome {
    if ('decline' in input) return { moneyDelta: 0, urgeDelta: 0, flags: offeredFlags(), logKey: 'minigame.pyramid.decline' };
    const invest = clampBet(Math.min(input.invest, state.maxInvest), life.money);
    if (invest <= 0) return { moneyDelta: 0, urgeDelta: 0, flags: offeredFlags(), logKey: 'minigame.pyramid.decline' };
    return {
      moneyDelta: -invest,
      urgeDelta: 0,
      flags: { ...offeredFlags(), [FLAG_PYRAMID_INVESTED]: invest, [FLAG_PYRAMID_YEARS]: 0 },
      logKey: 'minigame.pyramid.invest',
      logParams: { amount: Math.round(invest) },
    };
  },
  expected(): MinigameOutcome {
    return { moneyDelta: 0, urgeDelta: 0, flags: offeredFlags() };
  },
  expectancyText: (state: PyramidState): ExpectancyText => ({
    key: 'minigame.expect.pyramid',
    params: { yield: Math.round(state.promisedYield * 100), crash: Math.round(state.crashP * 100) },
  }),
};
